import * as React from 'react';
import { ProjectOverviewContentWrapper, ProjectOverviewWrapper } from './ProjectOverviewStyles';
import { language } from './ProjectOverviewLanguage';
import { GeneralViewPortLayout, Props as GeneralViewPortProps } from '../../Components/GeneralViewPort/GeneralViewPortLayout';
import { useMobile } from '../../utils/useMobile';
import { Toolbar } from '@mui/material';
import { Section } from './Layouts/Section';

export type Props = {};

const sectionsSchema = [
	{
		title: 'Cloud Platforms',
		subsections: [
			{
				title: 'Amazon Web Services (AWS)',
				bullets: ['Handled routing, throttling, and TLS Termination via Route 53 → API Gateway → Certificate Manager', 'Enforced secure routing and HTTPS via Route 53 → Certificate Manager', 'Served static resources via CloudFront → S3 to ensure fast, global access'],
				projects: [
					{
						text: 'KidiUp',
						bullets: ['Designed microservice infrastructure to support React-based web app', 'Implemented DynamoDB backed event-driven flows using SNS → SQS → Lambda for asynchronous tasks including notifications and billing'],
						tools: ['SNS', 'SQS', 'DynamoDB', 'Lambda', 'API Gateway', 'Route 53', 'Cloudfront', 'Cloudwatch', 'Cloud Formation', 'Code Pipeline', 'Code Commit', 'CodeBuild', 'CodeDeploy', 'CDK', 'SDK', 'IAM', 'Certificate Manager', 'Elasticache', 'S3'],
					},
					{
						text: 'Chowbotics',
						bullets: ['Designed microservice infrastructure to support Angular-based web app and React Native based mobile app', 'Architected DynamoDB backed event-driven backend to facilitate real-time inventory visibility and task tracking for Chowbotics kiosks via SNS → SQS → Lambda → DynamoDB', 'Improved fault tolerance by introducing retry logic and dead-letter queues for IoT and user-generated events'],
						tools: ['EBS', 'SNS', 'SQS', 'DynamoDB', 'Lambda', 'API Gateway', 'Route 53', 'Cloudfront', 'Cloudwatch', 'SDK', 'IAM', 'Certificate Manager', 'Elasticache', 'S3', 'Redshift', 'Redis'],
					},
					{
						text: 'Bamba',
						bullets: ['Handled 100 QPS ingestion of SMS-based user data via monolithic architecture ELB → EC2 → RDS pipeline', 'Scaled ingestion to 2,000+ QPS by migrating to  distributed event-driven architecture via SNS → SQS → Lambda → RDS pipeline', 'Enabled data egress to downstream partners and analytics stack using Kinesis and Lambda transformations'],
						tools: ['Route 53', 'RDS', 'EC2', 'ELB / ALB', 'SNS', 'SQS', 'SES', 'Lambda', 'API Gateway', 'Kinesis'],
					},
					{
						text: 'Cooking App',
						bullets: ['Designed microservice infrastructure to support React-based web app and React Native based mobile app', 'Set up static content delivery with CloudFront → S3 deployment for fast, global access'],
						tools: ['S3', 'Route 53', 'Cloudfront', 'RDS', 'SNS', 'SQS', 'DynamoDB', 'Lambda', 'API Gateway', 'Cloudwatch'],
					},
					{
						text: 'UX Blog',
						bullets: ['Designed full-stack serverless website for high-speed content delivery and minimal downtime using API Gateway, Lambda, DynamoDB, CloudFront and S3'],
						tools: ['S3', 'Route 53', 'Cloudfront', 'RDS', 'EC2', 'ELB / ALB', 'SNS', 'SQS', 'DynamoDB', 'Lambda', 'API Gateway'],
					},
				],
			},
			{
				title: 'Google Cloud Platform (GCP)',
				projects: [
					{
						text: 'The Helper Bees',
						bullets: ['Developed GCP-hosted microservices to support feature flag configuration and partner-specific behavior across the THB app suite', 'Used Pub/Sub → Google Functions for async task coordination and Firestore for low-latency config retrieval', 'Provisioned scalable cloud resources with Terraform and monitored health via Heartbeat monitor built as a Google Function'],
						tools: ['Google Functions', 'Blob Storage', 'Firestore', 'Compute Engine', 'Pub/Sub'],
					},
				],
			},
		],
	},
	{
		title: 'DevOps',
		subsections: [
			{
				title: 'Infrastructure',
				projects: [
					{
						text: 'KidiUp, Cooking App, UX Blog',
						bullets: ['Provisioned AWS infrastructure using CDK to standardize environments and enforce microservice isolation', 'Configured CloudWatch Alarms and Logs to monitor CI/CD pipelines and production workloads'],
						tools: ['AWS CDK'],
					},
					{
						text: 'Helper Bees',
						bullets: ['Provisioned GCP resources using Ansible and Terraform to ensure consistency across dev, staging, and prod', 'Orchestrated deployment workflows via CircleCI with environment-specific variables and secrets management'],
						tools: ['Ansible, Terraform, CircleCI'],
					},
				],
			},
			{
				title: 'Continuous Integration / Continuous Deployment',
				projects: [
					{
						text: 'KidiUp, Cooking App, UX Blog',
						bullets: ['Built CI/CD pipelines for both app and infra using CodeCommit, CodePipeline, CodeBuild, and CodeDeploy', 'Integrated IAM and Certificate Manager to enforce secure deploys and HTTPS across all environments'],
						tools: ['AWS CDK', 'CodeCommit', 'CodePipeline', 'CodeBuild', 'IAM', 'Certificate Manager'],
					},
				],
			},
		],
	},
	{
		title: 'Large Language Models',
		subsections: [
			{
				title: 'Open AI',
				bullets: ['Built structured LLM workflows using Assistants + Function Calling, with JSON-constrained outputs ingested downstream via SNS → Lambda → DynamoDB'],
				projects: [
					{
						text: 'KidiUp',
						bullets: ['Designed multi-agent LLM pipeline to search, identify, and extract Activity Center metadata (name, address, geo, blurb, schedule page) from search + crawl data', 'Built OpenAI-powered service to extract structured class schedules from unstructured HTML on Activity Center websites', 'Trained models to normalize inconsistent formatting — class names, age ranges, capitalization — across scraped sources', 'Captured website snapshots twice daily and passed them through the pipeline for extraction, enrichment, and ingestion'],
						tools: ['Chat Completions', 'Function Calling', 'Assistant API / SDK', 'Agent SDK', 'Fine Tuning', 'Embeddings', 'Vector Stores', 'System Prompts', 'Tool Use', 'Streaming Responses'],
					},
					{
						text: 'Cooking App',
						bullets: ['Built visual recipe assistant using OpenAI + image interpretation to guide users step-by-step through a cooking process', 'LLM interpreted user-uploaded progress photos and generated next-step instructions based on structured recipe context'],
						tools: ['Chat Completions', 'Function Calling', 'Assistant API / SDK', 'Fine Tuning', 'Tool Use'],
					},
					{
						text: 'UX Blog',
						bullets: ['Fine-tuned LLM to internalize the 11 Laws of UX and generate on-brand, high-signal blog entries', 'Generated structured JSON content drafts from loose prompts, suitable for direct integration with blog CMS'],
						tools: ['Chat Completions', 'Function Calling', 'Assistant API / SDK', 'Fine Tuning', 'Tool Use'],
					},
				],
			},
		],
	},
	{
		title: 'Image Generation',
		subsections: [
			{
				title: 'Diffusion Models',
				projects: [
					{
						text: 'Cooking App',
						bullets: ['Generated hero images for dish overview pages using Stable Diffusion, dynamically prompted with recipe instructions and ingredients', 'Used Assistant API to synthesize prompts when available, and fell back to ComfyUI pipeline when more nuanced control was needed', 'Built regeneration workflow and hosting pipeline via S3 and a dedicated microservice for repeatable image refresh'],
						tools: ['Stable Diffusion (ComfyUI)', 'DALL·E', 'Chat Completions', 'Function Calling'],
					},
					{
						text: 'UX Blog',
						bullets: ['Trained a LoRA model on cartoon  penguin to create a visual mascot that would be consistent across generations', 'Switched to DALL·E with prompt tuning once model quality surpassed SD + LoRA baseline'],
						tools: ['Stable Diffusion (ComfyUI)', 'LoRA Training', 'DALL·E', 'Chat Completions', 'Prompt Engineering'],
					},
				],
			},
		],
	},
	{
		title: 'Data Engineering',
		subsections: [
			{
				title: 'ETL Pipelines',
				projects: [
					{
						text: 'The Helper Bees',
						bullets: ['Ingested data from multiple health and insurance partners via secure file drops, APIs, and shared cloud storage', 'Validated, deduplicated, normalized and structured raw intake data into a unified internal format for downstream processing'],
						tools: ['Python', 'Flask', 'BigQuery'],
					},
					{
						text: 'Bamba',
						bullets: ['Transformed unstructured SMS survey responses into structured categorical data via intent-mapping and normalization', 'Implemented heuristics ML algorithm to detect and filter fraudulent, unreliable, and inconsistent user inputs'],
						tools: ['PHP', 'NodeJS', 'SQL'],
					},
					{
						text: 'Udemy - Data Science and Machine Learning Bootcamp with R',
						bullets: ['Used R for exploratory data analysis, data wrangling, and visualizations via ggplot2 and plotly', 'Explored core ML algorithms in R including linear regression, clustering, decision trees, and neural nets'],
						tools: ['R', 'ggplot2', 'plotly', 'dplyr', 'tidyr'],
					},
				],
			},
			{
				title: 'Data Warehousing',
				projects: [
					{
						text: 'Chowbotics Ingredient Synergy Warehouse',
						bullets: ['Transformed user input into structured records loaded into Redshift for real-time and historical analytics', 'Developed data structures and matrices in Redshift and Redis → Elasticache to power analytic dashboards and front end crowd-sourcing features'],
						tools: ['Redshift', 'Redis', 'SQL', 'Lambda', 'Python'],
					},
				],
			},
		],
	},
];

export const ProjectOverviewLayout = (props: Props) => {
	const isMobile = useMobile();
	const render = () => {
		const sectionsJSX = generateSections(sectionsSchema);
		const desktopJSX = (
			<ProjectOverviewWrapper>
				<ProjectOverviewContentWrapper>{sectionsJSX}</ProjectOverviewContentWrapper>
			</ProjectOverviewWrapper>
		);
		const mobileJSX = desktopJSX;

		const outgoingProps: GeneralViewPortProps = {
			internalJSX: isMobile ? mobileJSX : desktopJSX,
		};
		const returnValue = <GeneralViewPortLayout {...outgoingProps} />;
		return returnValue;
	};
	const outerReturnValue = render();
	return outerReturnValue;
};

export const generateSections = (sections) => {
	const returnValue = sections.map((section) => {
		const outgoingProps = {
			section,
		};
		const innerReturnValue = <Section {...outgoingProps} />;
		return innerReturnValue;
	});
	return returnValue;
};
